// Components/Tabs/ManageUsersTab.jsx

import React from "react";
import PropTypes from "prop-types";

// Importing components
import Slider from "../Slider/Slider";
import DataTable from "../DataTable/DataTable";
import Backend from "../../../utils/api.js";
import { useDocumentTitle } from "../../../utils/useDocumentTitle";

/**
 * ManageUsersTab Component
 * 
 * This component allows administrators to manage users within a specific lab.
 * It displays a data table of users with options to add or remove lab permissions.
 * 
 * @param {Object} props - Component properties.
 * @param {Object} props.user - The current user object containing user details.
 * @param {string} props.labId - The unique identifier of the lab.
 * @param {Function} props.pushTab - Function to push a new tab onto the stack.
 * @param {Function} props.revert - Function to revert the tab stack by a specified number of steps.
 */
function ManageUsersTab({ user, labId, pushTab, revert }) {

  useDocumentTitle("Admin: Manage space admins Tab – Pinpoint");
  return (
    <Slider
      name="Admin Details"
      navColor="rgb(50, 157, 237)"
      slide={true}
      index={0} // Starting index for the manage users tab
      onClick={() => revert(1)} // Revert one step when navigation bar is clicked
    >
      {/* DataTable displays the list of admins for the lab */}
      <DataTable
        title={`${labId} Admins`}
        columns={[
          { title: "User UID", field: "user", editable: "onAdd" },
          { title: "Directory ID", field: "directoryID", editable: "never" },
          { title: "First Name", field: "firstName", editable: "never" },
          { title: "Last Name", field: "lastName", editable: "never" },
        ]}
        dataFunction={() => Backend.getUsersByLab(user.UID, labId)} // Fetch data from backend
        error={
          "Action failed: The UID you provided was likely not found in the Pinpoint database. Register the user" +
          " with Pinpoint before adding them to a space."
        }
        submitRowDeletion={(oldPermission) =>
          // Demote rather than delete — the permission row also carries the
          // user's visit_count, which a hard delete would wipe.
          Backend.setLabPermissionAdmin(user.UID, oldPermission.accessID, false)
        }
        submitNewRow={(newPermission) =>
          Backend.newLabPermission(user.UID, newPermission.user, labId) // Handle adding new row
        }
      ></DataTable>
    </Slider>
  );
}

// PropTypes for type-checking component props
ManageUsersTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
};

export default ManageUsersTab;
